import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
    AfterViewInit,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { EventService } from "~app/common/services/cqs/event.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { StatedataService } from "~app/common/services/statedata.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IArbetsorderAtgarder } from "~app/features/planera/model/IArbetsorderAtgarder";
import { ArbetsorderHeaderComponent } from "../arbetsorder-header/arbetsorder-header.component";
import { VirkesorderStatusChangedEventMessage } from "../events/VirkesorderStatusChangedEventMessage";
import { DetailEvents } from "../events/detail-events";
import { ListEvents } from "../events/list-events";
import { MapEvents } from "../events/map-events";
import { Origin } from "../events/origin";
import { IMouseOverArbetsorderChangedEvent, ISelectAOEvent, PlaneraEvents } from "../events/planera-events";
import { PopupEvents } from "../events/popup-events";
import { PlaneraKartaEventService } from "../planera-karta/planera-karta-event.service";
import { ArbetsorderVolym } from "./arbetsorder-volym.model";

@Component({
    selector: "rask-arbetsorder-list",
    templateUrl: "./arbetsorder-list.component.html",
    styleUrls: ["./arbetsorder-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        ArbetsorderHeaderComponent
    ]
})
export class ArbetsorderListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    readonly state = inject(StatedataService);

    @Input() arbetsorderList: IArbetsorderAtgarder[];
    isSearchResult = false;
    @Input() listHeight = 200;

    /** lokal ao-list vi jobbar mot och som listas i cdk-virtual-scroll */
    localArbetsorderList: IArbetsorderAtgarder[];
    @Input() showOnlySelected: boolean;
    tableDataTotalAOVolumes: [any];
    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;

    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly planeraKartaBridgeEvents: IBridgeEvent[] = [];
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);

    private totalAOListVolumes: ArbetsorderVolym;

    constructor() {
        this.totalAOListVolumes = new ArbetsorderVolym();
    }

    @Input() set active(isActive: boolean)  {
        if (isActive === true) {
            this.signalSelected();
        }
    }

    aoIndex(index: any, ao: any) {
        return ao.beteckning;
    }

    ngAfterViewInit() {
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.mouseOverArbetsorder, (payload: IMouseOverArbetsorderChangedEvent) => {
            this.handleMouseOverArbetsorder(payload);
        }));

        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(PlaneraEvents.mouseOverArbetsorderTab, (payload: IMouseOverArbetsorderChangedEvent) => {
            this.handleMouseOverArbetsorder(payload);
        }));

        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.select, (payload: any) => {
            this.selectListItem(payload.beteckning, payload.selected);
        }));

        // Lyssnar på när hover sker i arbetsorder-header.component, som egentligen skickar det till kartan.
        // Vi jackar oss in och lyssnar på det också så att vi kan sätta hover i listan också
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(PlaneraEvents.mouseOverArbetsorderChanged, (payload: IMouseOverArbetsorderChangedEvent) => {
            const ao = this.localArbetsorderList.find(elem => elem.beteckning === payload.beteckning);
            if (ao) {
                ao.hover = payload.isMouseover;
            }
        }));

        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.selectMulti, (payload: any) => {
            if (payload.items) {
                const beteckningar = payload.items.map((i) => i.tag.beteckning);
                this.selectListItems(beteckningar, payload.selected);
            }
        }));

        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(DetailEvents.select, (payload: any) => {
            this.selectListItem(payload.beteckning, payload.selected);
        }));

        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(PopupEvents.select, (payload: any) => {
            this.selectListItem(payload.beteckning, payload.selected);
        }));
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(
            ListEvents.aoHeaderSelect, (payload: ISelectAOEvent) => {
                if (payload.origin === Origin.listHeader) {
                    this.selectRow(payload);
                }
            }
        ));
    }

    ngOnChanges(changesObj) {
        if (changesObj.arbetsorderList) {
            this.isSearchResult = !changesObj.arbetsorderList.firstChange;
            this.localArbetsorderList = changesObj.arbetsorderList.currentValue;
            if (this.showOnlySelected) {
                this.setOnlySelected();
            } else {
                this.setTraslagTotalList();
                this.emitAntal(this.arbetsorderList.length);
            }
        } else if (changesObj.showOnlySelected) {
            if (this.showOnlySelected) {
                this.setOnlySelected();
            } else {
                this.localArbetsorderList = this.arbetsorderList;
                const firstSelectedIndex = this.localArbetsorderList.findIndex(elem => elem.selected);
                this.scrollToIndex(firstSelectedIndex);
                this.setTraslagTotalList();
                const arbetsorderAntal = this.arbetsorderList.length - this.localArbetsorderList.filter(x => x.selected).length;
                this.emitAntal(arbetsorderAntal);
            }
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.planeraKartaEventService.unsubscribeMany(this.planeraKartaBridgeEvents);
        this.planeraKartaBridgeEvents.splice(0);
    }

    ngOnInit(): void {
        this.localArbetsorderList = [];
        this.populateTableData();
    }

    scrollToIndex(index: number): void {
        if (index > -1) {
            // Ugly med setTimeout, men det ser inte ut som att det går att göra på annat sätt
            // Problem: Om man har förändrat listan och vill scrolla till ett visst index så måste man vänta på nästa frame
            // Annars så scrollar den helt enkelt inte dit.
            setTimeout(() => {
                this.viewPort.scrollToIndex(index);
            });
        }
    }

    selectListItem(beteckning: string, selected: boolean) {
        const ao = this.localArbetsorderList.find(x => (x.beteckning === beteckning));
        ao.selected = selected;
        this.handleArbetsorderEventSubscription(ao.id, selected);
    }

    selectListItems(beteckningar: string[], selected: boolean) {
        this.arbetsorderList.forEach(x => {
            if (beteckningar.includes(x.beteckning)) {
                x.selected = selected;
                this.handleArbetsorderEventSubscription(x.id, selected);
            }
        });

        if (this.showOnlySelected) {
            this.setOnlySelected();
        } else {
            this.localArbetsorderList = this.arbetsorderList;
            this.setTraslagTotalList();
            this.emitAntal(this.localArbetsorderList.length);
        }
    }

    selectRow(event: ISelectAOEvent) {
        if (this.localArbetsorderList.find(x => x.beteckning === event.beteckning)) {
            // Kan vara redudant att göra selectListItem här, då ao.selected sätts i arbetsorder-header som är den enda komponent som triggar
            // eventet ListEvents.aoHeaderSelect med payload ISelectAOEvent
            this.selectListItem(event.beteckning, event.selected);
            event.origin = Origin.list;
            this.planeraKartaEventService.emit(
                PlaneraEvents.selectChanged,
                event
            );
            this.planeraKartaEventService.emit(
                ListEvents.select,
                event
            );

            if (!this.showOnlySelected) {
                // Behöver nog utredas vad antal skall vara i detta fall,
                // tidigare räknades antal ner oavsett om det var select eller de-select
                const arbetsorderAntal = this.arbetsorderList.length - this.localArbetsorderList.filter(x => x.selected).length;
                this.emitAntal(arbetsorderAntal);
            }
        }
        this.setTraslagTotalList();
    }

    readonly showTotalVolumes = () =>
        this.showOnlySelected
            ? this.localArbetsorderList.length > 1
            : this.arbetsorderList.length > 1;

    signalSelected() {
        const selectedIds = this.localArbetsorderList?.filter(x => x.selected === true)?.map(ao => ao.id);
        this.planeraKartaEventService.emit(MapEvents.selectArbetsordrar, selectedIds);
    }

    thousandSeparator(n: number) {
        let nStr = n.toString();
        const rgx = /(\d+)(\d{3})/;
        while (rgx.test(nStr)) {
            nStr = nStr.replace(rgx, "$1" + " " + "$2");
        }

        return nStr;
    }

    private emitAntal(arbetsorderAntal: number) {
        this.planeraKartaEventService.emit(
            ListEvents.arbetsorderListLength,
            arbetsorderAntal
        );
    }

    private handleArbetsorderEventSubscription(arbetsorderId: number, selected: boolean) {
        if(selected) {
            this.eventService.subscribeEvent<VirkesorderStatusChangedEventMessage>(VirkesorderStatusChangedEventMessage.$type)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .pipe(filter(event => event.arbetsorderId === arbetsorderId))
                .subscribe((event) => {
                    const arbetsorder = this.localArbetsorderList.find(y => y.id === arbetsorderId);
                    arbetsorder.aktuellVirkesorderStatus  = event.aktuellVirkesorderStatus;
                    arbetsorder.skickadTillViol = !ObjectUtils.isNullOrUndefined(event.senastAckadGeneration);
                    arbetsorder.voContractDirty = event.isDirty;
                });
        }
    }

    private handleMouseOverArbetsorder(payload: IMouseOverArbetsorderChangedEvent) {
        const selectedIndex = this.localArbetsorderList.findIndex(elem => elem.beteckning === payload.beteckning);
        const ao = this.localArbetsorderList.find(elem => elem.beteckning === payload.beteckning);
        if (ao) {
            ao.hover = payload.isMouseover;
        }
        if (_.isNumber(selectedIndex)) {
            this.scrollToIndex(selectedIndex);
        } else {
            console.error("unknown index");
        }
    }

    private populateTableData() {
        this.tableDataTotalAOVolumes = [
            {
                tt: this.thousandSeparator(this.totalAOListVolumes.tallTimmer),
                gt: this.thousandSeparator(this.totalAOListVolumes.granTimmer),
                ktt: this.thousandSeparator(this.totalAOListVolumes.klentimmerTall),
                kgt: this.thousandSeparator(this.totalAOListVolumes.klentimmerGran),
                bmav: this.thousandSeparator(this.totalAOListVolumes.barrmassaved),
                lmav: this.thousandSeparator(this.totalAOListVolumes.lovmassaved),
                total: this.thousandSeparator(this.totalAOListVolumes.totalVolym)
            }
        ];
    }

    private setOnlySelected() {
        this.localArbetsorderList = this.arbetsorderList.filter(x => x.selected);
        this.setTraslagTotalList();
        const arbetsorderAntal = this.localArbetsorderList.length;
        this.emitAntal(arbetsorderAntal);
    }

    private setTraslagTotalList() {
        this.totalAOListVolumes = new ArbetsorderVolym();
        const list: any[] = this.localArbetsorderList;

        list.forEach((ao) => {
            this.totalAOListVolumes.barrmassaved += ao.barrmassaved;
            this.totalAOListVolumes.granTimmer += ao.granTimmer;
            this.totalAOListVolumes.klentimmerGran += ao.klentimmerGran;
            this.totalAOListVolumes.klentimmerTall += ao.klentimmerTall;
            this.totalAOListVolumes.lovmassaved += ao.lovmassaved;
            this.totalAOListVolumes.tallTimmer += ao.tallTimmer;
            this.totalAOListVolumes.totalVolym += ao.volymArbetsorder;
        });

        this.populateTableData();
    }
}
